export const TOWER_MISSION = {
  payingUserTarget: 200,
  durationWeeks: 48,
  startDate: import.meta.env.VITE_TOWER_MISSION_START_DATE || '2026-09-07',
} as const;

export type HealthState = 'Healthy' | 'Watch' | 'At Risk';
export type HealthFactor = { label: string; points: number; detail: string };
export type HealthResult = { score: number; state: HealthState; factors: HealthFactor[] };

export function calculateCustomerHealth(input: {
  activeDays7: number; activeDays30: number; jobs30: number; invoices30: number;
  quotes30: number; expenses30: number; trendPercent: number | null;
  subscriptionStatus: string | null; daysInactive: number;
}): HealthResult {
  const factors: HealthFactor[] = [];
  const add = (label: string, points: number, detail: string) => factors.push({ label, points, detail });
  add('Recent activity', Math.min(25, input.activeDays7 * 4), `${input.activeDays7} active days in 7`);
  add('Monthly activity', Math.min(15, input.activeDays30), `${input.activeDays30} active days in 30`);
  add('Workflow volume', Math.min(25, input.jobs30 * 3 + input.invoices30 * 2 + input.quotes30 * 2 + input.expenses30), 'Jobs, invoices, quotes and expenses');
  add('Feature breadth', [input.jobs30, input.invoices30, input.quotes30, input.expenses30].filter(Boolean).length * 5, 'Meaningful features used');
  if (input.trendPercent !== null) add('Activity trend', input.trendPercent >= 0 ? 10 : Math.max(-20, input.trendPercent / 5), `${Math.round(input.trendPercent)}% vs own baseline`);
  if (input.subscriptionStatus && !['active', 'trialing'].includes(input.subscriptionStatus)) add('Subscription', -25, input.subscriptionStatus);
  if (input.daysInactive >= 7) add('Inactivity', -Math.min(30, input.daysInactive), `${input.daysInactive} days since meaningful activity`);
  const score = Math.max(0, Math.min(100, Math.round(factors.reduce((sum, factor) => sum + factor.points, 20))));
  return { score, state: score >= 70 ? 'Healthy' : score >= 40 ? 'Watch' : 'At Risk', factors };
}

export function isActivated(onboardingCompleted: boolean, firstJobAt: string | null): boolean {
  return onboardingCompleted && Boolean(firstJobAt);
}

export function isMeaningfullyActive(counts: { jobs: number; quotes: number; invoices: number; expenses: number }): boolean {
  return counts.jobs + counts.quotes + counts.invoices + counts.expenses > 0;
}
